import type { RouteRecordRaw } from 'vue-router'
import { ICON } from 'src/core/icons'
import { ticketLinkTarget } from 'src/modules/rules/lib/ticketLink'
import { viewerHasSession } from 'src/core/viewer'

/** The map editor's canvas modes render nothing of their own: the editor
 *  shell (MapEditorPage, the parent record) draws the canvas for them, so it
 *  stays mounted — camera, selection, unsaved changes — across a mode switch. */
const MapCanvasMode = { name: 'MapCanvasMode', render: () => null }

// ── The responsive contract (meta.minViewport) ──────────────────────────────
//
// Every routed page states the narrowest screen on which it is USABLE. Not
// "pretty" — usable. src/core/ViewportGate.vue turns a declaration the current
// window does not satisfy into a plain interstitial with an "Open anyway"
// escape, and src/router/viewport.invariant.test.ts fails the build for any
// routed page that has not declared at all (ui-conventions.md #90).
//
// Absence is a build failure rather than a default, because a default is
// precisely the failure mode this replaces. Before this, responsive coverage
// was accidental — 8 `@media` blocks and 6 of 173 components consulting
// `$q.screen` — and the user had no way to tell "this page is desktop-only by
// design" from "this page is broken". Both look like overlapping boxes. A
// silent default would have re-created that ambiguity in the source too: a
// page nobody had thought about would be indistinguishable from one somebody
// had decided about.
//
// ── Why three names and not Quasar's five ───────────────────────────────────
// Quasar's breakpoints (xs/sm/md/lg/xl) are a MEASUREMENT vocabulary. Asked to
// choose between `lg` and `md` for a page, nobody can defend the answer in
// review, and the distinction does not correspond to anything a product
// decision is actually made about. The decision being recorded here is
// genuinely ternary — does this work on a phone, does it need a tablet, does it
// need a real window — so those are the three words. `minViewport: 'phone'` is
// also the affirmative "works everywhere" declaration the contract needs;
// `minViewport: 'xs'` would read like an accident.
//
// The mapping onto Quasar's breakpoints lives in ONE place (ViewportGate's
// `actual`, off `$q.screen`), so `$q.screen` stays the single source of truth
// for what the viewport currently IS and this file only says what pages need.
export type MinViewport = 'phone' | 'tablet' | 'desktop'

/** Comparable order. Higher = needs more room. */
export const MIN_VIEWPORT_RANK: Record<MinViewport, number> = {
  phone: 0,
  tablet: 1,
  desktop: 2,
}

/** How each tier is phrased to a user, with the Quasar breakpoint it maps to. */
export const VIEWPORT_REQUIREMENT: Record<MinViewport, string> = {
  phone: 'any screen',
  tablet: 'a tablet-width screen (600px and up, Quasar `sm`)',
  desktop: 'a desktop-width window (1024px and up, Quasar `md`)',
}

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * The narrowest viewport this page is usable on. REQUIRED on every routed
     * page — optional here only because RouteMeta is shared with the framework's
     * own internal records; viewport.invariant.test.ts is what makes it
     * mandatory in practice.
     */
    minViewport?: MinViewport
  }
}

// The portal's route tree. Each entry's `meta.title`/`meta.icon` are read by
// the shell (top-bar title, nav rail) — see src/core/moduleRegistry.ts, which
// maps these route names to nav entries filtered by auth hints.
const routes: RouteRecordRaw[] = [
  // ── The public website (redesign batch 5, ui-conventions.md #158) ───────
  // Anonymous pages under PublicLayout: no session needed (publicPaths.ts),
  // an existing one is picked up on a direct load (session-aware), and every
  // query runs on the core-minted public token. FIRST in the list on purpose:
  // the signed-in shell's own `/` record matches `/` too, and on a tie the
  // earlier record wins — the public home must. `/documents` is the Rules &
  // documents page; the deep link below needs at least a season.
  {
    path: '/',
    component: () => import('layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'site-home',
        component: () => import('src/modules/site/SiteHomePage.vue'),
        meta: { title: 'Formula Student Germany', minViewport: 'phone' },
      },
      {
        path: 'event',
        name: 'site-event',
        component: () => import('src/modules/site/SiteEventPage.vue'),
        meta: { title: 'The event', minViewport: 'phone' },
      },
      {
        path: 'teams',
        name: 'site-teams',
        component: () => import('src/modules/site/SiteTeamsPage.vue'),
        meta: { title: 'Teams', minViewport: 'phone' },
      },
      {
        path: 'teams/:id',
        name: 'site-team',
        component: () => import('src/modules/site/SiteTeamPage.vue'),
        meta: { title: 'Team', minViewport: 'phone' },
      },
      {
        path: 'results/:competition?',
        name: 'site-results',
        component: () => import('src/modules/site/SiteResultsPage.vue'),
        meta: { title: 'Results', minViewport: 'phone' },
      },
      {
        path: 'documents',
        name: 'site-documents',
        component: () => import('src/modules/site/SiteDocumentsPage.vue'),
        meta: { title: 'Rules & documents', minViewport: 'phone' },
      },
      // ── The announcements board (#164 §5.6): the current event's
      // announcements, phone-first, anonymous (the public token). Dark until
      // cutover: nothing links here, and while the notification subgraph
      // refuses, the page shows a neutral "Not available" state. `competition`
      // is a competition slug (or id); absent = FSG. Public via
      // src/core/publicPaths.ts ('/today').
      {
        path: 'today/:competition?',
        name: 'today',
        component: () => import('src/modules/announcements/TodayPage.vue'),
        meta: { title: 'Announcements', minViewport: 'phone' },
      },
      // ── Partners (#168 AP6): the logo walls. Only `/partners` itself is the
      // app's (src/core/reservedPaths.ts APP_ROUTE_EXACT); the partner pages
      // below it (`/partners/bosch`) stay CMS pages at their TYPO3 paths.
      {
        path: 'partners',
        name: 'site-partners',
        component: () => import('src/modules/site/SitePartnersPage.vue'),
        meta: { title: 'Partners', minViewport: 'phone' },
      },
      // ── World › Competitions (#170): the map, the table and "At a glance"
      // from core. Only this one path is the app's (APP_ROUTE_EXACT); the
      // other pages of `/world/…` stay CMS pages.
      {
        path: 'world/competitions',
        name: 'site-competitions',
        component: () => import('src/modules/site/SiteCompetitionsPage.vue'),
        meta: { title: 'Competitions', minViewport: 'phone' },
      },
      // ── News (#162 AH6, contract §5.4) — the TYPO3 addresses, kept. They
      // sit inside the website's `/pr`, so src/core/reservedPaths.ts reserves
      // the deeper prefix `pr/news` (APP_ROUTE_PREFIXES): the rest of `/pr/…`
      // stays CMS pages, and these always win over the CMS catch-all.
      {
        path: 'pr/news',
        name: 'news',
        component: () => import('src/modules/news/NewsListPage.vue'),
        meta: { title: 'News', minViewport: 'phone' },
      },
      {
        path: 'pr/news/browse/:n(\\d+)',
        name: 'news-browse',
        component: () => import('src/modules/news/NewsListPage.vue'),
        meta: { title: 'News', minViewport: 'phone' },
      },
      {
        path: 'pr/news/category/:segment',
        name: 'news-category',
        component: () => import('src/modules/news/NewsListPage.vue'),
        meta: { title: 'News', minViewport: 'phone' },
      },
      {
        path: 'pr/news/archiv/:year(\\d{4})/:month(\\d{1,2})',
        name: 'news-archive',
        component: () => import('src/modules/news/NewsListPage.vue'),
        meta: { title: 'News', minViewport: 'phone' },
      },
      {
        path: 'pr/news/details/article/:slug',
        name: 'news-article',
        component: () => import('src/modules/news/NewsArticlePage.vue'),
        meta: { title: 'News', minViewport: 'phone' },
      },
    ],
  },
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      // ── The dual-audience public pages (#123 register S) ─────────────────
      // Public (src/core/publicPaths.ts), read by anonymous visitors AND by
      // signed-in people following a link from inside the portal — so they
      // live in the main layout, which serves both: the full shell for a
      // session, the same page with less chrome without one (S1(c), S4).
      // A direct load picks up an existing session silently (S2(d),
      // src/boot/keycloak.ts); the transport follows the viewer
      // (src/core/viewer.ts).
      {
        path: 'rules/faq',
        name: 'rules-faqs',
        component: () => import('src/modules/rules/RulesFaqRoute.vue'),
        // A signed-in user reads the FAQ inside the Rules tool.
        beforeEnter: () => (viewerHasSession() ? { name: 'rules-faq-tool' } : true),
        meta: { title: 'Rules FAQ', icon: ICON.rulesFaq, minViewport: 'phone' },
      },
      {
        path: 'rules/faq/:id',
        name: 'rules-faq-detail',
        component: () => import('src/modules/rules/RulesFaqRoute.vue'),
        beforeEnter: (to) =>
          viewerHasSession()
            ? { name: 'rules-faq-tool', params: { id: String(to.params.id ?? '') } }
            : true,
        meta: { title: 'Rules FAQ', icon: ICON.rulesFaq, minViewport: 'phone' },
      },
      {
        // Document-verification QR (fsgadmin/fsgnext#33, register D): the
        // legacy path VERBATIM — TYPO3 serves `/verify/qr/<qid>/<key>/`, and
        // D12 prints that scheme on new documents too, so the portal answers
        // the identical shape and the cutover redirect (#34) becomes a
        // path-preserving host rule instead of a rewrite. Both params
        // optional so the bare page still renders the manual-entry form.
        // Public — reached by a printed QR/direct link, never by nav —
        // so src/core/publicPaths.ts lists '/verify'.
        path: 'verify/qr/:qid?/:key?',
        name: 'verify-document',
        component: () => import('src/modules/verify/VerifyPage.vue'),
        meta: { title: 'Verify a document', minViewport: 'phone' },
      },
      {
        // The event map reader (#169 WP7, services/portal/docs/modules/map.md):
        // public under `/event` (src/core/publicPaths.ts), signed out on the
        // public token with the PUBLIC layers, signed in with the viewer's
        // layers (AO5). `?pit=09-C` focuses a pit (AO4), `?q=` searches,
        // `?phase=` picks the day layout (AO21), `?ev=` another event.
        path: 'event/map',
        name: 'event-map',
        component: () => import('src/modules/map/EventMapPage.vue'),
        meta: { title: 'Event map', icon: ICON.eventMap, minViewport: 'phone' },
      },
      {
        // The map editor (#169 WP8a/WP8b, services/portal/docs/modules/map.md
        // "The editor"): map.edit only (the page says so to anybody else);
        // `/tools/map` is reserved as a deeper prefix
        // (src/core/reservedPaths.ts). One shell (MapEditorPage) around the
        // mode tabs (owner ruling 2026-10-25): the canvas modes Shapes,
        // Markers, Tracks and Pits are drawn by the shell itself (their child
        // records only name the mode), Layers & phases, Import, Plans and
        // Settings are pages in its frame. The shell lives across the modes, so
        // the working copy and its unsaved changes do too. Phone/tablet: the
        // left panel is a bottom sheet over the canvas.
        path: 'tools/map',
        name: 'map-tools',
        component: () => import('src/modules/map/editor/MapEditorPage.vue'),
        redirect: (to) => ({ name: 'map-editor-shapes', query: to.query }),
        meta: { title: 'Map editor', icon: ICON.mapEditor, minViewport: 'phone' },
        children: [
          {
            // The pre-mode-tab editor's address: its deep links (`?ev`,
            // `?sel`, `?layer`) keep working — a selection of markers or pits
            // opens their mode (the shell switches).
            path: '',
            name: 'map-editor',
            redirect: (to) => ({ name: 'map-editor-shapes', query: to.query }),
            meta: { title: 'Map editor', icon: ICON.mapEditor, minViewport: 'phone' },
          },
          ...(
            [
              ['shapes', 'Shapes', ICON.mapShapes],
              ['markers', 'Markers', ICON.mapMarker],
              ['tracks', 'Tracks', ICON.mapTrack],
              ['pits', 'Pits', ICON.mapPit],
              ['buildings', 'Buildings', ICON.mapBuildings],
            ] as const
          ).map(([path, title, icon]) => ({
            path,
            name: `map-editor-${path}`,
            component: MapCanvasMode,
            meta: { title: `Map editor · ${title}`, icon, minViewport: 'phone' as const },
          })),
          {
            path: 'layers',
            name: 'map-editor-layers',
            component: () => import('src/modules/map/editor/MapLayersPage.vue'),
            meta: { title: 'Map layers & phases', icon: ICON.mapLayers, minViewport: 'phone' },
          },
          {
            path: 'import',
            name: 'map-editor-import',
            component: () => import('src/modules/map/editor/MapImportPage.vue'),
            meta: { title: 'Map import', icon: ICON.mapImport, minViewport: 'phone' },
          },
          {
            path: 'plans',
            name: 'map-editor-plans',
            component: () => import('src/modules/map/editor/MapPlansPage.vue'),
            meta: { title: 'Building plans', icon: ICON.mapPlans, minViewport: 'phone' },
          },
          {
            path: 'settings',
            name: 'map-editor-settings',
            component: () => import('src/modules/map/editor/MapSettingsPage.vue'),
            meta: { title: 'Map settings', icon: ICON.mapSettings, minViewport: 'phone' },
          },
        ],
      },
      {
        // TYPO3 serves the bare `/verify` as the manual-entry page and adds
        // `/qr/<qid>/<key>/` through a route enhancer, so both shapes must
        // answer here too — one canonical page, reached either way.
        path: 'verify',
        name: 'verify-document-entry',
        redirect: { name: 'verify-document' },
      },
      {
        // The portal's OWN /documents deep link (fsgadmin/fsgnext#69 §E4),
        // distinct from the printed website URL of the same shape — this one
        // is never printed on any physical/external artifact, so it carries
        // no D12 continuity obligation of its own. Public like /verify:
        // reached by a portal-to-portal or supergraph-field link, never by
        // nav, so src/core/publicPaths.ts lists '/documents'.
        //
        // NOTE — a gap in the frozen contract, routed back rather than
        // invented: §E4 as drafted (round 2) resolves this route through a
        // relaxed docstore documents() listing clamp that round 3's M19
        // explicitly WITHDRAWS (documents()/folders() stay admin-only for
        // every caller, full stop). Round 3 never restates E4's resolution
        // mechanism in light of that withdrawal. This implementation
        // resolves the route through the M11 publication plane instead
        // (Competition.publicationPages -> sections -> entries, matched by
        // season/kind/filename) — the only public-readable surface round 3
        // actually leaves standing — and flags the mismatch in the module
        // brief rather than resurrecting the withdrawn clamp.
        // Params optional so the bare '/documents' path (src/core/
        // publicPaths.ts's own entry) also resolves to this route, same
        // shape as '/verify' answering both its bare and qid/key forms —
        // the component shows its own "not found" state when they're absent.
        // `:season` required: a bare /documents is the public Rules &
        // documents page (PublicLayout), never a deep link to nothing.
        path: 'documents/:season/:kind?/:filename?',
        name: 'documents-deep-link',
        component: () => import('src/modules/docs/DocsDeepLinkPage.vue'),
        meta: { title: 'Document', minViewport: 'phone' },
      },
      {
        // My FSG (redesign batch 5): `/` became the public home, the
        // dashboard moved here. The route NAME is unchanged, so every named
        // link to it still lands.
        path: 'my',
        name: 'dashboard',
        component: () => import('src/modules/dashboard/DashboardPage.vue'),
        meta: { title: 'My FSG', icon: ICON.dashboard, minViewport: 'phone' },
      },
      {
        // Redesign batch 6: the viewer's team (moduleRegistry `team-home`).
        path: 'my/team',
        name: 'team-home',
        component: () => import('src/modules/team/MyTeamOverviewPage.vue'),
        meta: { title: 'My team', icon: ICON.team, minViewport: 'phone' },
      },
      // My team's other tabs (prototype parity T1: Overview / Members / Vehicle).
      {
        path: 'my/team/members',
        name: 'team-members',
        component: () => import('src/modules/team/MyTeamMembersPage.vue'),
        meta: { title: 'Members', icon: ICON.team, minViewport: 'phone' },
      },
      {
        path: 'my/team/vehicle',
        name: 'team-vehicle',
        component: () => import('src/modules/team/MyTeamVehiclePage.vue'),
        meta: { title: 'Vehicle', icon: ICON.vehicle, minViewport: 'phone' },
      },
      {
        // #165 (services/portal/docs/modules/statics-feedback.md): the team's static
        // statics feedback bookings — a phone page, teams book from the paddock.
        path: 'my/team/statics-feedback',
        name: 'team-statics-feedback',
        component: () => import('src/modules/staticsFeedback/TeamStaticsFeedbackPage.vue'),
        meta: { title: 'Statics feedback', icon: ICON.staticsFeedback, minViewport: 'phone' },
      },
      {
        // Redesign batch 6: Account › Appearance and sign-in, from the
        // account menu (not a rail module).
        path: 'my/account',
        name: 'account',
        component: () => import('src/modules/account/AccountPage.vue'),
        meta: { title: 'Account', icon: ICON.account, minViewport: 'phone' },
      },
      // The account's other tabs — the same page, the tab from the route
      // (prototype parity T1: Profile / Sign-in & security / Appearance /
      // Notifications).
      {
        path: 'my/account/security',
        name: 'account-security',
        component: () => import('src/modules/account/AccountPage.vue'),
        meta: { title: 'Sign-in & security', icon: ICON.password, minViewport: 'phone' },
      },
      {
        path: 'my/account/appearance',
        name: 'account-appearance',
        component: () => import('src/modules/account/AccountPage.vue'),
        meta: { title: 'Appearance', icon: ICON.appearance, minViewport: 'phone' },
      },
      {
        path: 'my/account/notifications',
        name: 'account-notifications',
        component: () => import('src/modules/account/AccountPage.vue'),
        meta: { title: 'Notifications', icon: ICON.messages, minViewport: 'phone' },
      },
      {
        path: 'core',
        name: 'core',
        component: () => import('src/modules/core/CoreLandingPage.vue'),
        redirect: { name: 'core-competitions' },
        meta: { title: 'Core', icon: ICON.core, minViewport: 'phone' },
        children: [
          {
            path: 'tenants',
            name: 'core-tenants',
            component: () => import('src/modules/core/TenantsPage.vue'),
            meta: { title: 'Tenants', icon: ICON.tenant, minViewport: 'phone' },
          },
          {
            path: 'competitions',
            name: 'core-competitions',
            component: () => import('src/modules/core/CompetitionsPage.vue'),
            meta: { title: 'Competitions', icon: ICON.competition, minViewport: 'phone' },
          },
          {
            path: 'events',
            name: 'core-events',
            component: () => import('src/modules/core/EventsPage.vue'),
            meta: { title: 'Events', icon: ICON.event, minViewport: 'phone' },
          },
          {
            path: 'teams',
            name: 'core-teams',
            component: () => import('src/modules/core/TeamsPage.vue'),
            meta: { title: 'Teams', icon: ICON.team, minViewport: 'phone' },
          },
          {
            path: 'universities',
            name: 'core-universities',
            component: () => import('src/modules/core/UniversitiesPage.vue'),
            meta: { title: 'Universities', icon: ICON.university, minViewport: 'phone' },
          },
          {
            path: 'vehicles',
            name: 'core-vehicles',
            component: () => import('src/modules/core/VehiclesPage.vue'),
            meta: { title: 'Vehicles', icon: ICON.vehicle, minViewport: 'phone' },
          },
          {
            // A participation as a record of its own (issue #138 F1).
            path: 'participations',
            name: 'core-participations',
            component: () => import('src/modules/core/ParticipationsPage.vue'),
            meta: { title: 'Participations', icon: ICON.participation, minViewport: 'phone' },
          },
          {
            path: 'persons',
            name: 'core-persons',
            component: () => import('src/modules/core/PersonsPage.vue'),
            meta: { title: 'Persons', icon: ICON.person, minViewport: 'phone' },
          },
          {
            path: 'roster',
            name: 'core-roster',
            component: () => import('src/modules/core/RosterPage.vue'),
            meta: { title: 'Roster', icon: ICON.roster, minViewport: 'phone' },
          },
          {
            // The Event office's volunteer roster (owner ruling 2026-10-18):
            // volunteers of one event with their jobs, days and decisions.
            path: 'volunteers',
            name: 'core-volunteers',
            component: () => import('src/modules/core/VolunteerRosterPage.vue'),
            meta: { title: 'Volunteer roster', icon: ICON.roster, minViewport: 'phone' },
          },
        ],
      },
      {
        // The member-documents reviewer queue (j55 DL-4). Its own entry, not a
        // Core tab: its audience is qual.review holders, who are not admins.
        path: 'member-documents',
        name: 'member-documents',
        component: () => import('src/modules/core/MemberDocumentsPage.vue'),
        meta: { title: 'Member documents', icon: ICON.memberDocuments, minViewport: 'phone' },
      },
      {
        // TABLET: the standings matrix has three sticky lead columns (left: 0 /
        // 40px / 72px) and an UNCAPPED team-name column that cannot wrap
        // (QTable's default `q-table--no-wrap`), against data-driven night/
        // deadline columns at `min-width: 48px` (DeadlineMatrixCell) ≈ 64px
        // each. At 360px a short team name leaves room for two cells and a long
        // one ("Formula Student Team TU Wien") leaves ZERO — the matrix stops
        // being a comparison on exactly the rows with the longest names. ~5
        // columns at 600px is a working matrix.
        // Weakest of the three exceptions, and the honest note is that it is a
        // fixable one: capping the team column would very likely make this
        // 'phone'. Declared tablet because that is true TODAY, not because the
        // page is inherently desktop.
        path: 'deadlines',
        name: 'deadlines',
        component: () => import('src/modules/deadlines/DeadlinesPage.vue'),
        meta: { title: 'Deadlines', icon: ICON.deadline, minViewport: 'tablet' },
      },
      {
        // DL-7 "Deadline setup": the admin definition editor. Admin-only via
        // the `admin` nav hint and the page's own post-sync redirect; every
        // write is requireAdmin-gated server-side.
        path: 'deadlines/definitions',
        name: 'deadlines-definitions',
        component: () => import('src/modules/deadlines/admin/DefinitionsAdminPage.vue'),
        meta: { title: 'Deadline setup', icon: ICON.deadlineSetup, minViewport: 'phone' },
      },
      {
        // DL-8: the Deadline setup page's Schedules view, same gate.
        path: 'deadlines/schedules',
        name: 'deadlines-schedules',
        component: () => import('src/modules/deadlines/admin/ScheduleGridPage.vue'),
        meta: { title: 'Deadline setup', icon: ICON.deadlineSetup, minViewport: 'phone' },
      },
      {
        // CO-9: the upload log across teams. Deadline staff (the `deadline`
        // hint); the server gates on the deadline read-all ceiling.
        path: 'deadlines/log',
        name: 'deadlines-log',
        component: () => import('src/modules/deadlines/admin/UploadLogPage.vue'),
        meta: { title: 'Upload log', icon: ICON.deadlineLog, minViewport: 'phone' },
      },
      {
        // Wide by nature — a column per answered documentation field — but
        // not broken narrow: the matrix scrolls inside its own box with the
        // car/team block pinned (#150), and the team detail becomes a
        // full-screen sheet below sm (#100). A dense wide table is a phone
        // page under the responsive contract; only genuine breakage is not.
        path: 'tvsd',
        name: 'tvsd',
        component: () => import('src/modules/tvsd/TvsdPage.vue'),
        meta: { title: 'TVSD', icon: ICON.tvsd, minViewport: 'phone' },
      },
      {
        // specs/archive/hotel/SPEC.md §8.5 — the nine ported /officials/hotel/* pages.
        // The shell (HotelLandingPage) owns the event selector and the live
        // lock banner every sub-page shares; each sub-page renders inside it.
        path: 'hotel',
        name: 'hotel',
        component: () => import('src/modules/hotel/HotelLandingPage.vue'),
        redirect: { name: 'hotel-user-data' },
        meta: { title: 'Hotel', icon: ICON.hotel, minViewport: 'phone' },
        children: [
          {
            path: 'user-data',
            name: 'hotel-user-data',
            component: () => import('src/modules/hotel/EditUserDataPage.vue'),
            meta: { title: 'Edit user data', icon: ICON.hotelUserData, minViewport: 'phone' },
          },
          {
            // TABLET: the bed grid pins three sticky columns at HARDCODED
            // offsets (left: 0 / 90px / 220px), so hotel+room+bed occupy a
            // fixed ~260px lead block before the first night column, and each
            // night cell is `min-width: 96px` + padding ≈ 112px. That is 372px
            // to see a room's identity and ONE night, against the 312px a 360px
            // phone actually offers inside the page padding — not one night
            // column can ever be fully visible, and the nights slide
            // permanently underneath the sticky block. Broken, not cramped.
            // 600px leaves ~292px ≈ 2.6 nights with the identity intact.
            path: 'guests-in-rooms',
            name: 'hotel-guests-edit',
            component: () => import('src/modules/hotel/GuestsInRoomsEditPage.vue'),
            meta: {
              title: 'Edit Guests in Room Data',
              icon: ICON.hotelBeds,
              minViewport: 'tablet',
            },
          },
          {
            path: 'todo',
            name: 'hotel-todo',
            component: () => import('src/modules/hotel/BookingTodoPage.vue'),
            meta: { title: 'Booking to-do', icon: ICON.hotelTodo, minViewport: 'phone' },
          },
          {
            path: 'runs',
            name: 'hotel-runs',
            component: () => import('src/modules/hotel/SubmissionRunsPage.vue'),
            meta: { title: 'Submission runs', icon: ICON.hotelRun, minViewport: 'phone' },
          },
          {
            path: 'assigned-users',
            name: 'hotel-assigned',
            component: () => import('src/modules/hotel/ListAssignedUsersPage.vue'),
            meta: { title: 'Assigned users', icon: ICON.hotelReport, minViewport: 'phone' },
          },
          {
            path: 'assigned-users-todo',
            name: 'hotel-assigned-todo',
            component: () => import('src/modules/hotel/ListAssignedUsersTodoPage.vue'),
            meta: {
              title: 'Assigned users to-do',
              icon: ICON.hotelReport,
              minViewport: 'phone',
            },
          },
          {
            path: 'booked-rooms',
            name: 'hotel-booked-rooms',
            component: () => import('src/modules/hotel/ListBookedRoomsPage.vue'),
            meta: { title: 'Booked rooms', icon: ICON.hotelRooms, minViewport: 'phone' },
          },
          {
            path: 'guests-report',
            name: 'hotel-guests-list',
            component: () => import('src/modules/hotel/ListGuestsInRoomsPage.vue'),
            meta: {
              title: 'Guests in rooms (report)',
              icon: ICON.hotelReport,
              minViewport: 'phone',
            },
          },
          {
            path: 'catalogues',
            name: 'hotel-catalogues',
            component: () => import('src/modules/hotel/CataloguesPage.vue'),
            meta: {
              title: 'List Hotel Details / Catalogues',
              icon: ICON.hotel,
              minViewport: 'phone',
            },
          },
        ],
      },
      {
        // specs/archive/rbac/SPEC.md (register Z) — the platform.admin access-control
        // module. Same shell-with-children shape as core/hotel; the nav entry
        // is gated on the platform.admin ceiling hint
        // (src/core/moduleRegistry.ts), and every operation under it is
        // server-enforced through Authorizer.IsPlatformAdmin regardless.
        path: 'rbac',
        name: 'rbac',
        component: () => import('src/modules/rbac/RbacLandingPage.vue'),
        redirect: { name: 'rbac-groups' },
        meta: { title: 'Access control', icon: ICON.rbac, minViewport: 'phone' },
        children: [
          {
            // The group detail is addressed by PATH (`/rbac/groups/:id`, the
            // module brief's own route table), so a group is linkable as
            // itself. Optional param, one route: list and detail share a view
            // state (filters live in the query and travel with the
            // selection), which two route names would split in half.
            path: 'groups/:id?',
            name: 'rbac-groups',
            component: () => import('src/modules/rbac/GroupsPage.vue'),
            meta: { title: 'Groups', icon: ICON.rbacGroup, minViewport: 'phone' },
          },
          {
            // The hierarchy graph (fsgadmin/fsgnext#3, register G).
            // Same optional-param contract as the groups screen: a selected
            // node is a linkable location, filters travel in the query.
            // DESKTOP — the only one in the portal, and the only genuine
            // canvas. Group cards are a hard pixel constant (graphModel.ts's
            // `NODE_WIDTH = 260`, applied as an exact `width: ${…}px` box), and
            // the region deliberately never scrolls: it pans and zooms
            // (`overflow: hidden` + `fit-view-on-init`, `min-zoom: 0.1`). At
            // 312px exactly ONE card fits at zoom 1, and fit-view on a real
            // hierarchy drops to ~0.3 where 12px labels render at ~4px. Both
            // end states destroy the only thing the page exists to show. A
            // parent over two children needs ~850px of content, so the
            // threshold is genuinely desktop and not tablet — tablet buys a
            // second card, which is still not a graph.
            //
            // This costs no function: the same nesting data is fully reachable
            // at phone width through GroupsPage's detail panel ("nesting" tab).
            path: 'graph/:id?',
            name: 'rbac-graph',
            component: () => import('src/modules/rbac/GraphPage.vue'),
            meta: { title: 'Graph', icon: ICON.rbacGraph, minViewport: 'desktop' },
          },
          {
            path: 'assignments',
            name: 'rbac-assignments',
            component: () => import('src/modules/rbac/AssignmentsPage.vue'),
            meta: { title: 'Assignments', icon: ICON.rbacAssignment, minViewport: 'phone' },
          },
          {
            path: 'grants',
            name: 'rbac-grants',
            component: () => import('src/modules/rbac/GrantsPage.vue'),
            meta: { title: 'Direct grants', icon: ICON.rbacGrant, minViewport: 'phone' },
          },
          {
            path: 'service-principals',
            name: 'rbac-service-principals',
            component: () => import('src/modules/rbac/ServicePrincipalsPage.vue'),
            meta: { title: 'Service principals', icon: ICON.rbacService, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/tickets.md — the operator Ticket
        // Center (ticketing turnover, specs/archive/ticket/SPEC.md). Same
        // shell-with-children shape as core/hotel/rbac; the admin sub-pages
        // (bulk/types/audit) are ticket.admin surfaces, server-enforced —
        // a deep link without the role lands on the screen's own error
        // state carrying the server's refusal (#34).
        path: 'tickets',
        name: 'tickets',
        component: () => import('src/modules/tickets/TicketsLandingPage.vue'),
        redirect: { name: 'tickets-center' },
        meta: { title: 'Tickets', icon: ICON.ticket, minViewport: 'phone' },
        children: [
          {
            // TABLET, and unlike the other two this is ONE toolbar row, not a
            // layout: the scan field is `width: 300px; flex: 0 0 auto` (shrink
            // disabled) inside a `row items-center no-wrap` that also carries
            // the Dymo station bar (`min-width: 200px` printer + `width: 160px`
            // station) and the New-ticket button — ~534px of min-content
            // against 312px. The parent is `.page-fill { overflow: hidden }`,
            // so the excess is CLIPPED with no scrollbar and "New ticket"
            // becomes unreachable rather than merely offscreen. The table
            // itself is fine at any width.
            // Cheapest exception to retire in the whole file: dropping
            // `no-wrap` on that row would make this 'phone'.
            path: 'center',
            name: 'tickets-center',
            component: () => import('src/modules/tickets/TicketCenterPage.vue'),
            meta: { title: 'Ticket center', icon: ICON.ticket, minViewport: 'tablet' },
          },
          {
            // PHONE, deliberately — recorded because this is the page everyone
            // assumes is desktop-only (it is the print sheet) and would
            // otherwise "fix" by declaring it so. It already carries a real
            // phone breakpoint: `@media (max-width: 859px)` neutralizes the
            // `flex: 0 0 440px` left column and its `min-width: 320px`, the two
            // columns stack, and the body becomes the single scroll region. Its
            // tables are `q-markup-table`, which scrolls internally. Somebody
            // already did this work; do not undo it with a declaration.
            path: 'bulk',
            name: 'tickets-bulk',
            component: () => import('src/modules/tickets/BulkPrintPage.vue'),
            meta: { title: 'Bulk print', icon: ICON.ticketBulk, minViewport: 'phone' },
          },
          {
            path: 'types',
            name: 'tickets-types',
            component: () => import('src/modules/tickets/TicketTypesPage.vue'),
            meta: { title: 'Ticket types', icon: ICON.ticketType, minViewport: 'phone' },
          },
          {
            path: 'audit',
            name: 'tickets-audit',
            component: () => import('src/modules/tickets/TicketAuditPage.vue'),
            meta: { title: 'Audit log', icon: ICON.ticketAudit, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/sponsors.md — sponsors admin. The nav
        // LINK is gated on `sponsor.manage` (moduleRegistry + auth.ts
        // canManageSponsors); the route is not — every read and write is
        // server-enforced.
        path: 'sponsors',
        name: 'sponsors',
        component: () => import('src/modules/sponsors/SponsorsAdminPage.vue'),
        meta: { title: 'Sponsors', icon: ICON.sponsor, minViewport: 'phone' },
      },
      {
        // services/portal/docs/modules/audit.md — cross-service audit viewer.
        // The nav LINK is gated on the admin / read.all ceiling
        // (auth.canReadAudit); the page redirects out after the Me sync, and
        // globalAuditLog enforces the same gate server-side.
        path: 'audit',
        name: 'audit',
        component: () => import('src/modules/audit/AuditPage.vue'),
        meta: { title: 'Audit', icon: ICON.audit, minViewport: 'phone' },
      },
      {
        // services/portal/docs/modules/expenses.md — the reimbursement
        // module (fsgadmin/fsgnext#25, register G).
        //
        // The parent record carries NO component on purpose: `/expenses` IS
        // the list (module brief §Screens), not a shell that redirects to a
        // sub-page the way core/hotel/rbac/tickets do. A component-less
        // parent is still a matched record, which is what reveals the rail's
        // "Review" sub-item while anywhere under /expenses
        // (MainLayout.isActiveModule reads `route.matched`), and its
        // `redirect` is what the nav link's `{ name: 'expenses' }` resolves
        // through.
        //
        // G26: the module is reachable at its named route for any
        // authenticated principal — no guard, no redirect. The nav LINK is
        // gated (src/core/moduleRegistry.ts), the route is not; every
        // operation underneath is server-enforced regardless.
        path: 'expenses',
        name: 'expenses',
        redirect: { name: 'expenses-list' },
        meta: { title: 'Expenses', icon: ICON.expense, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'expenses-list',
            component: () => import('src/modules/expenses/ExpensesListPage.vue'),
            meta: { title: 'Expenses', icon: ICON.expense, minViewport: 'phone' },
          },
          {
            // Mints a draft and REPLACES itself with /expenses/:id — see the
            // page's own header for why the editor cannot exist without an
            // envelope id.
            path: 'new',
            name: 'expenses-new',
            component: () => import('src/modules/expenses/ExpenseEditorPage.vue'),
            meta: { title: 'New expense', icon: ICON.add, minViewport: 'phone' },
          },
          {
            path: 'review',
            name: 'expenses-review',
            component: () => import('src/modules/expenses/ExpenseReviewPage.vue'),
            meta: { title: 'Expense review', icon: ICON.expenseReview, minViewport: 'phone' },
          },
          {
            path: 'review/payout',
            name: 'expenses-payout',
            component: () => import('src/modules/expenses/ExpensePayoutPage.vue'),
            meta: { title: 'Payout', icon: ICON.expensePayout, minViewport: 'phone' },
          },
          {
            // Last: a param segment must not shadow `new`/`review`. (Route
            // ranking already prefers the static ones; the ordering says so
            // to the next reader too.)
            path: ':id',
            name: 'expenses-detail',
            component: () => import('src/modules/expenses/ExpenseEditorPage.vue'),
            meta: { title: 'Expense', icon: ICON.expense, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/finance-admin.md — the finance.admin
        // back office (fsgadmin/fsgnext#25, register G, G33).
        //
        // ROUTES AND NAV ONLY. The page components live in
        // src/modules/finance/ and are built by the finance-admin work
        // package; this file is owned by the expenses/shell package so the
        // two never contend for it. If an import below does not resolve, the
        // finance module has not landed yet — the fix is that module, never
        // an edit here.
        //
        // Same component-less-parent shape as /expenses above, and for the
        // same reason: `/finance` IS the overview (module brief: "overview is
        // the landing"), and the parent record is what reveals the rail's
        // sub-items across the whole subtree.
        path: 'finance',
        name: 'finance',
        redirect: { name: 'finance-overview' },
        meta: { title: 'Finance', icon: ICON.finance, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'finance-overview',
            component: () => import('src/modules/finance/FinanceOverviewPage.vue'),
            meta: { title: 'Finance', icon: ICON.finance, minViewport: 'phone' },
          },
          {
            path: 'orders',
            name: 'finance-orders',
            component: () => import('src/modules/finance/OrdersPage.vue'),
            meta: { title: 'Orders', icon: ICON.financeOrders, minViewport: 'phone' },
          },
          {
            path: 'orders/:id',
            name: 'finance-order',
            component: () => import('src/modules/finance/OrderDetailPage.vue'),
            meta: { title: 'Order', icon: ICON.financeOrders, minViewport: 'phone' },
          },
          {
            path: 'queue',
            name: 'finance-queue',
            component: () => import('src/modules/finance/InvoiceQueuePage.vue'),
            meta: { title: 'Invoice queue', icon: ICON.financeQueue, minViewport: 'phone' },
          },
          {
            path: 'refunds',
            name: 'finance-refunds',
            component: () => import('src/modules/finance/RefundsPage.vue'),
            meta: { title: 'Refunds', icon: ICON.financeRefunds, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/rules.md — the FS Rules history
        // explorer (fsgadmin/fsgnext#68, register B, B18). Read-only: the
        // whole plane is a materialized copy of an external dataset and has
        // no mutations at all.
        //
        // Shell + three children, and the `:edition/:number?` record is ONE
        // route on purpose: the outline and the rule are one screen, and
        // useViewState keys a screen's URL state by route NAME — two names
        // would drop the outline's expand state every time a rule opened
        // (see src/modules/rules/lib/route.ts' own note).
        //
        // The nav LINK is gated on `rules.read` (moduleRegistry + auth.ts
        // canReadRules); the ROUTE is not, like every other module here —
        // every operation under it is server-enforced (B13: every rules root
        // calls Check(ActRead, KindRules)).
        path: 'rules',
        name: 'rules',
        component: () => import('src/modules/rules/RulesRoute.vue'),
        meta: { title: 'Rules', icon: ICON.rules, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'rules-index',
            component: () => import('src/modules/rules/RulesIndexPage.vue'),
            meta: { title: 'Rules', icon: ICON.rules, minViewport: 'phone' },
          },
          {
            // Before the param route, so `/rules/search` can never be read as
            // an edition named "search". (Route ranking already prefers the
            // static segment; the ordering says so to the next reader too.)
            path: 'search',
            name: 'rules-search',
            component: () => import('src/modules/rules/RulesSearchPage.vue'),
            meta: { title: 'Search the rules', icon: ICON.search, minViewport: 'phone' },
          },
          {
            path: 'questions',
            name: 'rules-questions',
            component: () => import('src/modules/rules/RulesQuestionsPage.vue'),
            meta: { title: 'Rules questions', icon: ICON.rulesQuestion, minViewport: 'phone' },
          },
          {
            path: 'questions/:ref',
            name: 'rules-question-detail',
            component: () => import('src/modules/rules/RulesQuestionDetailPage.vue'),
            meta: { title: 'Rules question', icon: ICON.rulesQuestion, minViewport: 'phone' },
          },
          {
            path: 'feedback',
            name: 'rules-feedback',
            component: () => import('src/modules/rules/RulesFeedbackPage.vue'),
            meta: { title: 'Rules feedback', icon: ICON.rulesFeedback, minViewport: 'phone' },
          },
          {
            path: 'feedback/:ref',
            name: 'rules-feedback-detail',
            component: () => import('src/modules/rules/RulesFeedbackDetailPage.vue'),
            meta: { title: 'Rules feedback', icon: ICON.rulesFeedback, minViewport: 'phone' },
          },
          {
            // Deep links services/rules sends in mails (R-…/F-…) and the
            // GitLab mirror (numeric ticket id). A display ref redirects
            // here; an id is looked up by the page. Before `:edition`, which
            // would otherwise read "tickets" as an edition.
            path: 'tickets/:ref',
            name: 'rules-ticket',
            component: () => import('src/modules/rules/RulesTicketLinkPage.vue'),
            beforeEnter: (to) => {
              const target = ticketLinkTarget(String(to.params.ref ?? ''))
              return target && 'name' in target ? { ...target, replace: true } : true
            },
            meta: { title: 'Rules ticket', icon: ICON.rulesQuestion, minViewport: 'phone' },
          },
          {
            // The FAQ inside the tool frame for signed-in users; the public
            // /rules/faq sends them here and serves anonymous readers itself.
            // Before `:edition`, which would otherwise read "faqs" as an
            // edition.
            path: 'faqs/:id?',
            name: 'rules-faq-tool',
            component: () => import('src/modules/rules/RulesFaqsPage.vue'),
            meta: { title: 'Rules FAQ', icon: ICON.rulesFaq, minViewport: 'phone' },
          },
          {
            path: 'review',
            name: 'rules-review',
            component: () => import('src/modules/rules/RulesReviewerConsolePage.vue'),
            meta: { title: 'Rules review queue', icon: ICON.rulesReview, minViewport: 'phone' },
          },
          {
            // The reviewers' experts directory (j55 WP RU-12). Before
            // `:edition`, which would otherwise read "experts" as an edition.
            path: 'experts',
            name: 'rules-experts',
            component: () => import('src/modules/rules/RulesExpertsPage.vue'),
            meta: { title: 'Rules experts', icon: ICON.person, minViewport: 'phone' },
          },
          {
            path: ':edition/:number?',
            name: 'rules-edition',
            component: () => import('src/modules/rules/RulesBrowsePage.vue'),
            // The three-column reading layout collapses itself below
            // THREE_COLUMN_MIN_WIDTH (RulesBrowsePage's own `wide` computed),
            // so it does not need an exception. Measured in headless Chromium
            // at 360px: zero window overflow and zero elements overhanging the
            // viewport, on this route and its two siblings.
            meta: { title: 'Rules', icon: ICON.rules, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/docs.md — the public-website
        // documents manager (fsgadmin/fsgnext#69, register M). Two screens:
        // the fileadmin-like browse/upload/manage tree, and the season-page
        // publication view (ordered sections, reorder, unpublish).
        //
        // The nav LINK is gated on `docs.public.manage` (moduleRegistry +
        // auth.ts canManageDocs); the ROUTE is not, like every other module
        // here — every mutation is server-enforced.
        path: 'docs',
        name: 'docs',
        component: () => import('src/modules/docs/DocsLandingPage.vue'),
        meta: { title: 'Documents', icon: ICON.docs, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'docs-browse',
            component: () => import('src/modules/docs/DocsBrowsePage.vue'),
            // Genuinely broken below tablet, not merely cramped — see
            // router/viewport.invariant.test.ts's EXCEPTIONS entry for the
            // arithmetic (the fixed-width folder tree eats most of a phone
            // viewport).
            meta: { title: 'Documents', icon: ICON.docsFolder, minViewport: 'tablet' },
          },
          {
            path: 'publications',
            name: 'docs-publications',
            component: () => import('src/modules/docs/DocsPublicationsPage.vue'),
            // Tablet since the hierarchy rework (owner ruling 2026-08-29):
            // the page rail is the same shape of fixed lead block the browse
            // tree is, and 91 pages need it. Same arithmetic, same
            // EXCEPTIONS entry.
            meta: { title: 'Publications', icon: ICON.docsPublications, minViewport: 'tablet' },
          },
        ],
      },
      {
        // services/portal/docs/modules/scoring.md — the officials' scoring
        // desk (fsgadmin/fsgnext#85, WP10; program epic #75): the input
        // plane, the write-ahead-log history with undo, the penalty-case
        // workflow, result/running-order/award publishing, and the gocas
        // evaluation-trace explorer.
        //
        // Component-less parent, the expenses/finance shape: `/scoring` IS
        // the results screen (the brief's route table), not a shell that
        // redirects to a sub-page the way core/hotel/rbac/tickets do. The
        // parent record is still MATCHED, which is what reveals the rail's
        // nine sibling sub-items while anywhere under /scoring
        // (MainLayout.isActiveModule reads `route.matched`), and its
        // `redirect` is what the nav link's `{ name: 'scoring' }` resolves
        // through.
        //
        // No param segments anywhere in this subtree, on purpose: the
        // module's selection (context, event, discipline, row) travels in
        // query params — `ctx`/`ev`/`disc`/`sel` — because a scoring screen's
        // anchor is view state shared BETWEEN screens (#1: switching
        // sub-page must not lose the event you are working on), not an
        // address of its own. See lib/useScoringAnchor.ts.
        //
        // The nav LINK is gated (moduleRegistry + auth.ts canSeeScoring /
        // canReadScoringFull); the ROUTE is not, like every other module
        // here — every read and write underneath is server-enforced, and a
        // caller holding no scoring grant is refused the whole plane.
        path: 'scoring',
        name: 'scoring',
        redirect: { name: 'scoring-results' },
        meta: { title: 'Scoring', icon: ICON.scoring, minViewport: 'phone' },
        children: [
          {
            // PHONE (module brief §Parity class): a read surface — standings
            // and a per-discipline results grid. #105 already scrolls a wide
            // table inside its own container and #100 makes the row-detail
            // sidebar a full-screen sheet below `sm`, so "many columns" is
            // not a reason to lock it out of a phone. An official checking a
            // standing from the paddock is the case this serves.
            path: '',
            name: 'scoring-results',
            component: () => import('src/modules/scoring/ScoringResultsPage.vue'),
            meta: { title: 'Results', icon: ICON.scoringResults, minViewport: 'phone' },
          },
          {
            path: 'inputs',
            name: 'scoring-inputs',
            component: () => import('src/modules/scoring/ScoringInputsPage.vue'),
            meta: { title: 'Scoring inputs', icon: ICON.scoringInputs, minViewport: 'tablet' },
          },
          {
            path: 'history',
            name: 'scoring-history',
            component: () => import('src/modules/scoring/ScoringHistoryPage.vue'),
            meta: { title: 'Change history', icon: ICON.scoringHistory, minViewport: 'tablet' },
          },
          {
            path: 'penalties',
            name: 'scoring-penalties',
            component: () => import('src/modules/scoring/ScoringPenaltiesPage.vue'),
            meta: { title: 'Penalties', icon: ICON.scoringPenalties, minViewport: 'tablet' },
          },
          {
            path: 'publications',
            name: 'scoring-publications',
            component: () => import('src/modules/scoring/ScoringPublicationsPage.vue'),
            meta: {
              title: 'Publications',
              icon: ICON.scoringPublications,
              minViewport: 'tablet',
            },
          },
          {
            path: 'runorder',
            name: 'scoring-runorder',
            component: () => import('src/modules/scoring/ScoringRunorderPage.vue'),
            meta: { title: 'Running order', icon: ICON.scoringRunorder, minViewport: 'tablet' },
          },
          {
            path: 'awards',
            name: 'scoring-awards',
            component: () => import('src/modules/scoring/ScoringAwardsPage.vue'),
            meta: { title: 'Special awards', icon: ICON.scoringAwards, minViewport: 'tablet' },
          },
          {
            // PHONE, with Results: the C8 trace explorer is a READING
            // surface — a formula, a runs table, an overlay panel and a list
            // of intermediates, stacked. It is the screen an official opens
            // beside a protest, wherever they happen to be standing.
            path: 'traces',
            name: 'scoring-traces',
            component: () => import('src/modules/scoring/ScoringTracesPage.vue'),
            meta: { title: 'Evaluation traces', icon: ICON.scoringTraces, minViewport: 'phone' },
          },
          {
            path: 'config',
            name: 'scoring-config',
            component: () => import('src/modules/scoring/ScoringConfigPage.vue'),
            meta: {
              title: 'Scoring configuration',
              icon: ICON.scoringConfig,
              minViewport: 'tablet',
            },
          },
          {
            path: 'audit',
            name: 'scoring-audit',
            component: () => import('src/modules/scoring/ScoringAuditPage.vue'),
            meta: { title: 'Scoring audit', icon: ICON.scoringAudit, minViewport: 'tablet' },
          },
        ],
      },
      {
        // services/portal/docs/modules/ceremony.md — the Award ceremony tool
        // (#163 AI1). The program editor degrades to read-only at phone width
        // (declared in the brief), so every page stays a phone page.
        path: 'ceremony',
        name: 'ceremony',
        redirect: { name: 'ceremony-program' },
        meta: { title: 'Award ceremony', icon: ICON.ceremony, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'ceremony-program',
            component: () => import('src/modules/ceremony/CeremonyProgramPage.vue'),
            meta: { title: 'Ceremony program', icon: ICON.ceremonyProgram, minViewport: 'phone' },
          },
          {
            path: 'season',
            name: 'ceremony-season',
            component: () => import('src/modules/ceremony/CeremonySeasonPage.vue'),
            meta: { title: 'Ceremony season', icon: ICON.ceremonySeason, minViewport: 'phone' },
          },
          {
            path: 'run-of-show',
            name: 'ceremony-runofshow',
            component: () => import('src/modules/ceremony/CeremonyRunOfShowPage.vue'),
            meta: { title: 'Run of show', icon: ICON.ceremonyRunOfShow, minViewport: 'phone' },
          },
          {
            path: 'themes',
            name: 'ceremony-themes',
            component: () => import('src/modules/ceremony/CeremonyThemesPage.vue'),
            meta: { title: 'Ceremony themes', icon: ICON.ceremonyThemes, minViewport: 'phone' },
          },
          {
            path: 'audit',
            name: 'ceremony-audit',
            component: () => import('src/modules/ceremony/CeremonyAuditPage.vue'),
            meta: { title: 'Ceremony audit log', icon: ICON.ceremonyAudit, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/statics-feedback.md — the officials' Statics feedback
        // tool (#165 AK8). Phone pages: the timeline scrolls sideways inside
        // its own box, the forms stack.
        path: 'statics-feedback',
        name: 'statics-feedback',
        redirect: { name: 'statics-feedback-schedule' },
        meta: { title: 'Statics feedback', icon: ICON.staticsFeedback, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'statics-feedback-schedule',
            component: () => import('src/modules/staticsFeedback/StaticsFeedbackSchedulePage.vue'),
            meta: {
              title: 'Statics feedback schedule',
              icon: ICON.staticsFeedbackSchedule,
              minViewport: 'phone',
            },
          },
          {
            path: 'teams',
            name: 'statics-feedback-teams',
            component: () => import('src/modules/staticsFeedback/StaticsFeedbackTeamsPage.vue'),
            meta: { title: 'Statics feedback by team', icon: ICON.team, minViewport: 'phone' },
          },
          {
            path: 'setup',
            name: 'statics-feedback-setup',
            component: () => import('src/modules/staticsFeedback/StaticsFeedbackSetupPage.vue'),
            meta: {
              title: 'Statics feedback setup',
              icon: ICON.staticsFeedbackSetup,
              minViewport: 'phone',
            },
          },
          {
            path: 'print',
            name: 'statics-feedback-print',
            component: () => import('src/modules/staticsFeedback/StaticsFeedbackPrintPage.vue'),
            meta: {
              title: 'Statics feedback print',
              icon: ICON.staticsFeedbackPrint,
              minViewport: 'phone',
            },
          },
        ],
      },
      {
        // services/portal/docs/modules/notification.md (NO-2) and
        // notification-admin.md (NO-4). The component-less parent is the
        // Messages module's tree (same shape as /expenses): `/messages` IS the
        // inbox, and the parent record is what reveals the rail's sub-items
        // anywhere under /messages. No guard on either route: the inbox is
        // self-service for every authenticated principal, and the notification
        // resolvers refuse a non-admin on the mail log, which the page shows.
        path: 'messages',
        name: 'messages',
        redirect: { name: 'messages-inbox' },
        meta: { title: 'Messages', icon: ICON.messages, minViewport: 'phone' },
        children: [
          {
            // `?id=<inbox item id>` opens that message (the dashboard card's
            // rows link here).
            path: '',
            name: 'messages-inbox',
            component: () => import('src/modules/notification/MessagesPage.vue'),
            meta: { title: 'Messages', icon: ICON.messages, minViewport: 'phone' },
          },
          {
            path: 'admin',
            name: 'messages-admin',
            component: () => import('src/modules/notification/AdminLogPage.vue'),
            meta: { title: 'Mail log', icon: ICON.messagesAdmin, minViewport: 'phone' },
          },
        ],
      },
      {
        // services/portal/docs/modules/press.md (j55 HT-11/HT-12): the press
        // office's queue at /press, the applicant's own page at /press/apply.
        path: 'press',
        name: 'press',
        redirect: { name: 'press-accreditations' },
        meta: { title: 'Press', icon: ICON.press, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'press-accreditations',
            component: () => import('src/modules/press/PressAccreditationsPage.vue'),
            meta: { title: 'Press accreditations', icon: ICON.pressQueue, minViewport: 'phone' },
          },
          {
            path: 'apply',
            name: 'press-apply',
            component: () => import('src/modules/press/PressApplyPage.vue'),
            meta: { title: 'Press accreditation', icon: ICON.pressApply, minViewport: 'phone' },
          },
        ],
      },
      {
        // #160 WP8b — the Website tool (prototype `tool-cms`): the page tree
        // with the menu and the footer, the image library, and — for cms.admin
        // — audiences, redirects and the cms audit log. `/website` is reserved
        // in src/core/reservedPaths.ts, so no CMS page can take it.
        path: 'website',
        name: 'website',
        redirect: { name: 'website-pages' },
        meta: { title: 'Website', icon: ICON.website, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'website-pages',
            component: () => import('src/modules/cms/site/WebsitePagesPage.vue'),
            meta: { title: 'Website pages', icon: ICON.websitePages, minViewport: 'phone' },
          },
          {
            path: 'images',
            name: 'website-images',
            component: () => import('src/modules/cms/site/WebsiteImagesPage.vue'),
            meta: { title: 'Website images', icon: ICON.websiteImages, minViewport: 'phone' },
          },
          {
            path: 'audiences',
            name: 'website-audiences',
            component: () => import('src/modules/cms/site/WebsiteAudiencesPage.vue'),
            meta: { title: 'Audiences', icon: ICON.websiteAudiences, minViewport: 'phone' },
          },
          {
            path: 'redirects',
            name: 'website-redirects',
            component: () => import('src/modules/cms/site/WebsiteRedirectsPage.vue'),
            meta: { title: 'Redirects', icon: ICON.websiteRedirects, minViewport: 'phone' },
          },
          {
            path: 'audit',
            name: 'website-audit',
            component: () => import('src/modules/cms/site/WebsiteAuditPage.vue'),
            meta: { title: 'Website audit log', icon: ICON.websiteAudit, minViewport: 'phone' },
          },
        ],
      },
      {
        // #164 — the Announcements tool (announcement.publish, tenant feature
        // `announcements` on): the list (Drafts / Scheduled / Published /
        // Archive, default audience) and the editor. `/announcements` is
        // reserved in src/core/reservedPaths.ts; the public board is /today.
        path: 'announcements',
        name: 'announcements',
        component: () => import('src/modules/announcements/AnnouncementsPage.vue'),
        meta: { title: 'Announcements', icon: ICON.announcements, minViewport: 'phone' },
      },
      {
        path: 'announcements/new',
        name: 'announcement-new',
        component: () => import('src/modules/announcements/AnnouncementEditorPage.vue'),
        meta: { title: 'New announcement', icon: ICON.announcements, minViewport: 'phone' },
      },
      {
        path: 'announcements/:id',
        name: 'announcement-edit',
        component: () => import('src/modules/announcements/AnnouncementEditorPage.vue'),
        meta: { title: 'Announcement', icon: ICON.announcements, minViewport: 'phone' },
      },
      {
        // #162 — the News tool (news.manage; platform admins pass): the
        // article list with its state chips, the article editor (draft →
        // publish / schedule, history, slug change, delete) and the category
        // catalogue. `/newsroom` is reserved in src/core/reservedPaths.ts;
        // the public pages are /pr/news (above, PublicLayout).
        path: 'newsroom',
        name: 'newsroom',
        redirect: { name: 'newsroom-articles' },
        meta: { title: 'News', icon: ICON.news, minViewport: 'phone' },
        children: [
          {
            path: '',
            name: 'newsroom-articles',
            component: () => import('src/modules/news/desk/NewsDeskPage.vue'),
            meta: { title: 'News articles', icon: ICON.newsArticles, minViewport: 'phone' },
          },
          {
            path: 'articles/new',
            name: 'newsroom-new',
            component: () => import('src/modules/news/desk/NewsEditorPage.vue'),
            meta: { title: 'New article', icon: ICON.newsArticles, minViewport: 'phone' },
          },
          {
            path: 'articles/:id',
            name: 'newsroom-article',
            component: () => import('src/modules/news/desk/NewsEditorPage.vue'),
            meta: { title: 'News article', icon: ICON.newsArticles, minViewport: 'phone' },
          },
          {
            path: 'categories',
            name: 'newsroom-categories',
            component: () => import('src/modules/news/desk/NewsCategoriesPage.vue'),
            meta: { title: 'News categories', icon: ICON.newsCategories, minViewport: 'phone' },
          },
        ],
      },
      {
        // specs/archive/ui-reports/SPEC.md §Portal work package — the trusted-tester
        // problem-report pool (R5: one screen, everyone with the read gate
        // sees all reports; triage controls appear inline for platform.admin).
        path: 'reports',
        name: 'reports',
        component: () => import('src/modules/reports/ReportsRoute.vue'),
        meta: { title: 'Reports', icon: ICON.report, minViewport: 'phone' },
      },
    ],
  },
  // The identity funnel (fsgadmin/fsgnext#2 §8): sign-in, claim, sign-up,
  // recovery, the setup wizard and the account-model page. Its own layout,
  // because it serves people who have no account (or no session) yet, and
  // src/core/publicPaths.ts must list exactly these paths, since that is what
  // tells the auth boot to skip Keycloak. Public pages that signed-in people
  // read too live in the main layout instead (#123 S4).
  {
    path: '/',
    component: () => import('layouts/AuthLayout.vue'),
    children: [
      {
        path: 'signin',
        name: 'identity-signin',
        component: () => import('src/modules/identity/SignInPage.vue'),
        meta: { title: 'Sign in', minViewport: 'phone' },
      },
      {
        path: 'claim',
        name: 'identity-claim',
        component: () => import('src/modules/identity/ClaimPage.vue'),
        meta: { title: 'Claim your account', minViewport: 'phone' },
      },
      {
        path: 'signup',
        name: 'identity-signup',
        component: () => import('src/modules/identity/SignUpPage.vue'),
        meta: { title: 'Create an account', minViewport: 'phone' },
      },
      {
        path: 'recovery',
        name: 'identity-recovery',
        component: () => import('src/modules/identity/RecoveryPage.vue'),
        meta: { title: 'Account recovery', minViewport: 'phone' },
      },
      {
        path: 'setup',
        name: 'identity-setup',
        component: () => import('src/modules/identity/SetupPage.vue'),
        meta: { title: 'Set up sign-in', minViewport: 'phone' },
      },
      {
        path: 'account-info',
        name: 'identity-info',
        component: () => import('src/modules/identity/AccountInfoPage.vue'),
        meta: { title: 'How accounts work', minViewport: 'phone' },
      },
    ],
  },
  {
    // ── The CMS catch-all (#160 AF10) — MUST stay the LAST record ─────────
    // Every path no route above claimed may be a CMS page at its TYPO3 path
    // (`/fsg/accommodation`), read through the cms service (CmsPageRoute).
    // The app always wins: a path whose first segment is reserved
    // (src/core/reservedPaths.ts — the first segments of every route above,
    // kept in step by src/router/cmsCatchAll.invariant.test.ts) is never
    // looked up, and with the tenant's `cms` feature off, or no page at the
    // path, this answers the portal's 404 page. Public: a
    // CMS candidate path is session-aware public (src/core/publicPaths.ts).
    //
    // A "not found" message is the one page in the portal that must render
    // everywhere without argument — gating it behind a width would answer a
    // mistyped URL on a phone with a second thing the user cannot read.
    path: '/:cmsPath(.*)*',
    component: () => import('layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'cms-page',
        component: () => import('src/modules/cms/CmsPageRoute.vue'),
        meta: { title: 'Formula Student Germany', minViewport: 'phone' },
      },
    ],
  },
]

export default routes
